import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import BrandMark from "@/components/BrandMark";

export default function Navbar({ variant = "transparent" }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const solid = variant === "solid" || scrolled;

    const links = [
        { to: "/#services", label: "Services" },
        { to: "/#pricing", label: "Pricing" },
        { to: "/#faq", label: "FAQ" },
        { to: "/rewards", label: "Rewards" },
        { to: "/gift", label: "Gift" },
        { to: "/#contact", label: "Contact" },
    ];

    return (
        <nav
            data-testid="main-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                solid ? "bg-navy/95 backdrop-blur-md border-b border-gold/30" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo">
                    <BrandMark variant="navbar" />
                </Link>

                <div className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        l.to.includes("#") ? (
                            <a
                                key={l.to}
                                href={l.to}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                className="text-cream font-outfit text-sm tracking-wide hover:text-gold transition-colors uppercase"
                            >
                                {l.label}
                            </a>
                        ) : (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                className="text-cream font-outfit text-sm tracking-wide hover:text-gold transition-colors uppercase"
                            >
                                {l.label}
                            </Link>
                        )
                    ))}
                    <Link
                        to="/book"
                        data-testid="nav-book-now-btn"
                        className="relative bg-gold text-navy px-6 py-2.5 font-outfit font-semibold text-sm tracking-wide uppercase overflow-hidden group transition-all hover:shadow-[0_0_24px_rgba(212,175,55,0.55)]"
                    >
                        <span className="relative z-10">Book Now</span>
                        <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent group-hover:translate-x-full transition-transform duration-700" />
                    </Link>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    className="md:hidden text-gold"
                    onClick={() => setOpen(!open)}
                    aria-label="Menu"
                >
                    {open ? <X /> : <Menu />}
                </button>
            </div>

            {open && (
                <div className="md:hidden bg-navy border-t border-gold/30 px-6 py-6 flex flex-col gap-4">
                    {links.map((l) => (
                        l.to.includes("#") ? (
                            <a
                                key={l.to}
                                href={l.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                className="text-cream font-outfit text-sm uppercase tracking-wide hover:text-gold"
                            >
                                {l.label}
                            </a>
                        ) : (
                            <Link
                                key={l.to}
                                to={l.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                className="text-cream font-outfit text-sm uppercase tracking-wide hover:text-gold"
                            >
                                {l.label}
                            </Link>
                        )
                    ))}
                    <Link
                        to="/book"
                        onClick={() => setOpen(false)}
                        data-testid="mobile-nav-book-now-btn"
                        className="bg-gold text-navy px-6 py-3 font-outfit font-semibold text-sm tracking-wide uppercase text-center"
                    >
                        Book Now
                    </Link>
                </div>
            )}
        </nav>
    );
}
